export interface JobCategoryDefinition {
  slug: string;
  name: string;
  type: "category" | "employment-type" | "student";
  shortDescription: string;
  heroHeadline: string;
  commonWorkplaces: string[];
  keySkills: string[];
  entryRequirements: string;
  sampleResponsibilities: string[];
  interviewTips: string[];
  outreachTip: string;
  relatedCategorySlugs: string[];
}

export const JOB_CATEGORIES_REGISTRY: JobCategoryDefinition[] = [
  // --- JOB CATEGORIES ---
  {
    slug: "retail-jobs",
    name: "Retail Jobs",
    type: "category",
    shortDescription: "Discover local retail positions at independent boutiques, hardware stores, specialty shops, and local chains.",
    heroHeadline: "Find Retail Jobs & Store Hiring Signals Near You",
    commonWorkplaces: ["Independent Boutiques", "Hardware Stores", "Specialty Retailers", "Local Clothing Stores", "Bookstores"],
    keySkills: ["Customer Service", "Point of Sale (POS)", "Inventory Management", "Visual Merchandising", "Communication"],
    entryRequirements: "Entry-level friendly. No prior experience required for floor associate or cashier roles.",
    sampleResponsibilities: [
      "Assist shoppers with product inquiries and location",
      "Process retail transactions and handle cash/card payments",
      "Restock shelves and maintain visually attractive store displays",
      "Receive inventory shipments and organize backroom stock"
    ],
    interviewTips: [
      "Emphasize a welcoming, customer-first attitude",
      "Highlight reliability, punctuality, and flexible availability during weekend peak hours",
      "Mention any experience handling money, team sports, or group projects"
    ],
    outreachTip: "Visit local retail shops early in the week (Tuesday through Thursday morning) before weekend customer rushes begin.",
    relatedCategorySlugs: ["cashier-jobs", "stocking-jobs", "customer-service-jobs", "part-time-jobs"]
  },
  {
    slug: "restaurant-jobs",
    name: "Restaurant Jobs",
    type: "category",
    shortDescription: "Explore restaurant hiring at local diners, family eateries, upscale dining rooms, and neighborhood cafes.",
    heroHeadline: "Explore Local Restaurant Roles & Kitchen Positions Near You",
    commonWorkplaces: ["Local Family Diners", "Boutique Restaurants", "Cafes & Bistros", "Sports Grills", "Catering Companies"],
    keySkills: ["Active Listening", "Food Safety & Sanitation", "Multi-Tasking", "Teamwork", "High-Energy Customer Service"],
    entryRequirements: "Roles ranging from zero experience (dishwasher, busser, host) to experienced (line cook, lead server).",
    sampleResponsibilities: [
      "Greet guests and seat dining parties efficiently",
      "Take food and beverage orders accurately and communicate with kitchen staff",
      "Prepare food items, handle line cooking, and maintain kitchen sanitation",
      "Clear, sanitize, and reset dining tables quickly between seatings"
    ],
    interviewTips: [
      "Demonstrate high energy, calm under pressure, and a strong work ethic",
      "Ask about shift schedules and food safety guidelines used on-site",
      "Show willingness to help out wherever needed during busy dining rushes"
    ],
    outreachTip: "Stop by between 2:00 PM and 4:00 PM when restaurants are between lunch and dinner service.",
    relatedCategorySlugs: ["server-jobs", "kitchen-jobs", "cook-jobs", "dishwasher-jobs", "host-jobs"]
  },
  {
    slug: "fast-food-jobs",
    name: "Fast Food Jobs",
    type: "category",
    shortDescription: "Find fast food, drive-thru, and quick-service restaurant positions with immediate hiring potential.",
    heroHeadline: "Quick Service & Fast Food Hiring Near You",
    commonWorkplaces: ["Local Franchises", "Drive-Thru Restaurants", "Fast-Casual Eateries", "Juice & Smoothie Bars"],
    keySkills: ["Speed & Accuracy", "Point of Sale (POS)", "Food Preparation", "Team Coordination", "Punctuality"],
    entryRequirements: "100% entry-level friendly. Great first job for high school and college students.",
    sampleResponsibilities: [
      "Take customer orders at front counter or drive-thru headset",
      "Operate fryers, grills, and beverage dispensers following exact recipes",
      "Maintain strict food temperature logs and kitchen hygiene standards",
      "Keep dining and counter areas sanitized and well-stocked"
    ],
    interviewTips: [
      "Highlight speed, attention to detail, and enthusiasm for fast-paced work",
      "Confirm availability for evening, weekend, or opening shifts",
      "Express interest in growing into team leader or shift supervisor positions"
    ],
    outreachTip: "Ask for the General Manager during mid-morning hours (9:30 AM – 11:00 AM).",
    relatedCategorySlugs: ["crew-member-jobs", "cashier-jobs", "cook-jobs", "first-jobs", "teen-jobs"]
  },
  {
    slug: "grocery-store-jobs",
    name: "Grocery Store Jobs",
    type: "category",
    shortDescription: "Search positions at local independent supermarkets, organic markets, delis, and specialty food stores.",
    heroHeadline: "Local Grocery Store & Supermarket Jobs Near You",
    commonWorkplaces: ["Independent Supermarkets", "Specialty Food Markets", "Neighborhood Delis", "Organic Grocers"],
    keySkills: ["Stocking & Merchandising", "Food Safety", "Cash Handling", "Customer Assistance", "Physical Stamina"],
    entryRequirements: "No prior experience required for stocking, cashiering, or bagging roles.",
    sampleResponsibilities: [
      "Scan items accurately at checkout and bag groceries for customers",
      "Unload delivery trucks and stock refrigerated, frozen, and dry goods",
      "Maintain clean aisles, organize produce displays, and check expiration dates",
      "Prepare fresh items in bakery, deli, or meat departments"
    ],
    interviewTips: [
      "Mention comfort with physical tasks like lifting boxes and standing for long shifts",
      "Highlight friendly demeanor when helping shoppers find items",
      "Emphasize reliable attendance record"
    ],
    outreachTip: "Ask for department managers (Produce, Meat, Deli, Front End) directly on weekday mornings.",
    relatedCategorySlugs: ["stocking-jobs", "cashier-jobs", "bakery-jobs", "part-time-jobs"]
  },
  {
    slug: "warehouse-jobs",
    name: "Warehouse Jobs",
    type: "category",
    shortDescription: "Find fulfillment, material handling, loading, and logistics positions at nearby distribution centers.",
    heroHeadline: "Warehouse, Fulfillment & Material Handling Jobs",
    commonWorkplaces: ["Local Logistics Hubs", "Wholesale Distributors", "Fulfillment Centers", "Freight Terminals"],
    keySkills: ["Order Picking", "Inventory Control", "Safety Awareness", "Forklift Operation", "Attention to Detail"],
    entryRequirements: "Entry-level roles available. On-the-job training provided for picking and packing.",
    sampleResponsibilities: [
      "Pick items from warehouse racks using handheld RF scanners",
      "Pack orders into boxes, apply shipping labels, and inspect for accuracy",
      "Unload freight trucks, stage pallets, and organize inventory stacks",
      "Operate manual pallet jacks or motorized material handling equipment"
    ],
    interviewTips: [
      "Stress physical stamina, safety consciousness, and punctuality",
      "Mention any certifications (forklift, OSHA 10) or willingness to complete training",
      "Highlight ability to meet hourly picking accuracy targets"
    ],
    outreachTip: "Contact warehouse operations managers early in the shift before shipping volume peaks.",
    relatedCategorySlugs: ["stocking-jobs", "delivery-jobs", "full-time-jobs", "night-jobs"]
  },
  {
    slug: "construction-jobs",
    name: "Construction Jobs",
    type: "category",
    shortDescription: "Connect with local general contractors, home builders, roofers, and specialty trade companies.",
    heroHeadline: "Local Construction & General Contractor Jobs Near You",
    commonWorkplaces: ["General Contractors", "Residential Builders", "Roofing Companies", "Paving & Site Contractors"],
    keySkills: ["Power Tool Handling", "Blueprint Reading", "Site Safety", "Physical Strength", "Troubleshooting"],
    entryRequirements: "Laborer positions require no prior experience. Trade apprenticeships available.",
    sampleResponsibilities: [
      "Prepare job sites, unload materials, and set up staging equipment",
      "Operate hand and power tools safely under job site supervision",
      "Perform framing, demolition, drywall, painting, or concrete work",
      "Maintain clean work areas and adhere to strict safety regulations"
    ],
    interviewTips: [
      "Focus on physical fitness, dependability, and willingness to learn trade skills",
      "Bring up reliable personal transportation to variable job sites",
      "Mention any safety equipment (steel-toe boots, hard hat) you already own"
    ],
    outreachTip: "Reach out to job site superintendents or crew chiefs around 6:30 AM before daily work begins.",
    relatedCategorySlugs: ["laborer-jobs", "maintenance-jobs", "landscaping-jobs", "full-time-jobs"]
  },
  {
    slug: "healthcare-jobs",
    name: "Healthcare Jobs",
    type: "category",
    shortDescription: "Discover employment at local medical clinics, urgent care centers, dental offices, and eldercare facilities.",
    heroHeadline: "Healthcare & Medical Office Jobs Near You",
    commonWorkplaces: ["Independent Medical Clinics", "Urgent Care Facilities", "Dental Practices", "Physical Therapy Centers"],
    keySkills: ["Patient Care", "HIPAA Compliance", "Medical Terminology", "Empathy & Communication", "EHR Software"],
    entryRequirements: "Roles available for both certified staff (CNA, MA, RN) and entry-level non-clinical support (front desk, patient scheduler).",
    sampleResponsibilities: [
      "Check in patients, take vital signs, and update electronic health records",
      "Assist healthcare providers during routine exams and procedures",
      "Manage medical scheduling, insurance verification, and billing inquiries",
      "Maintain sterile, organized clinical rooms and medical equipment"
    ],
    interviewTips: [
      "Emphasize patient privacy awareness, compassion, and precise record-keeping",
      "Detail relevant certifications (CPR, CNA, CMA, Phlebotomy)",
      "Highlight ability to stay calm in urgent patient situations"
    ],
    outreachTip: "Contact practice managers on Tuesday or Wednesday afternoon after morning patient check-in rushes.",
    relatedCategorySlugs: ["medical-office-jobs", "dental-office-jobs", "pharmacy-jobs", "receptionist-jobs"]
  },
  {
    slug: "office-jobs",
    name: "Office Jobs",
    type: "category",
    shortDescription: "Explore administrative, customer care, data entry, and office coordination roles at local businesses.",
    heroHeadline: "Office, Administrative & Corporate Support Roles Near You",
    commonWorkplaces: ["Accounting Firms", "Law Offices", "Insurance Agencies", "Real Estate Firms", "Local Corporate Hubs"],
    keySkills: ["Microsoft Office / Google Workspace", "Data Entry", "Written Communication", "Phone Etiquette", "Organization"],
    entryRequirements: "Entry-level administrative support roles require basic computer literacy and strong communication skills.",
    sampleResponsibilities: [
      "Answer incoming phone calls, route messages, and respond to email inquiries",
      "Organize physical and digital files, maintain calendars, and schedule meetings",
      "Perform precise data entry, invoice processing, and document formatting",
      "Welcome office visitors and manage office supply inventories"
    ],
    interviewTips: [
      "Highlight typing speed, software familiarity, and attention to detail",
      "Dress professionally and communicate clearly and articulate thoughts smoothly",
      "Bring physical copies of your resume formatted cleanly"
    ],
    outreachTip: "Reach out to Office Managers or Practice Directors via email or phone call during mid-morning hours.",
    relatedCategorySlugs: ["receptionist-jobs", "administrative-jobs", "customer-service-jobs", "full-time-jobs"]
  },
  {
    slug: "customer-service-jobs",
    name: "Customer Service Jobs",
    type: "category",
    shortDescription: "Find customer-facing, call center, desk support, and client relationship roles at local enterprises.",
    heroHeadline: "Customer Service & Client Support Jobs Near You",
    commonWorkplaces: ["Local Service Agencies", "Support Call Centers", "Retail Service Counters", "Insurance Offices"],
    keySkills: ["Problem Solving", "Conflict De-escalation", "Active Listening", "CRM Software", "Patience"],
    entryRequirements: "Entry-level friendly. Training provided on product knowledge and support software.",
    sampleResponsibilities: [
      "Respond to customer inquiries via phone, email, live chat, or in-person",
      "Troubleshoot account issues, process returns, and resolve service complaints",
      "Document interactions accurately in customer relationship management (CRM) software",
      "Educate clients on product features and promotional offers"
    ],
    interviewTips: [
      "Share real examples of resolving a difficult customer situation calmly",
      "Highlight strong active listening and problem-solving skills",
      "Express enthusiasm for helping people"
    ],
    outreachTip: "Inquire with local business service managers mid-week during non-peak call hours.",
    relatedCategorySlugs: ["retail-jobs", "receptionist-jobs", "administrative-jobs", "part-time-jobs"]
  },
  {
    slug: "receptionist-jobs",
    name: "Receptionist Jobs",
    type: "category",
    shortDescription: "Find front desk, client greeting, and administrative support roles across local offices and clinics.",
    heroHeadline: "Front Desk & Receptionist Jobs Near You",
    commonWorkplaces: ["Medical & Dental Practices", "Law Offices", "Hair Salons & Spas", "Auto Dealerships", "Corporate Offices"],
    keySkills: ["Front Desk Operations", "Appointment Scheduling", "Multi-Line Phone Systems", "Professional Greeting", "Filing"],
    entryRequirements: "High school diploma or equivalent. Friendly professional demeanor essential.",
    sampleResponsibilities: [
      "Greet clients and visitors warmly as they arrive at the facility",
      "Answer multi-line telephone systems, answer questions, and direct calls",
      "Manage appointment calendars and send reminder notifications",
      "Accept deliveries, handle incoming mail, and maintain reception area cleanliness"
    ],
    interviewTips: [
      "Display an approachable, polished first impression during the interview",
      "Demonstrate strong organization and ability to balance walk-in visitors with phone calls",
      "Mention familiarity with office software and appointment scheduling systems"
    ],
    outreachTip: "Walk into local offices during mid-afternoon hours when front desks are quiet.",
    relatedCategorySlugs: ["office-jobs", "administrative-jobs", "medical-office-jobs", "entry-level-jobs"]
  },
  {
    slug: "cashier-jobs",
    name: "Cashier Jobs",
    type: "category",
    shortDescription: "Search checkout, register operation, and payment processing roles at local retail and food businesses.",
    heroHeadline: "Cashier & Register Associate Jobs Near You",
    commonWorkplaces: ["Grocery Stores", "Hardware Stores", "Gas Stations & Convenience Stores", "Fast Food Chains"],
    keySkills: ["Cash Register Operation", "Basic Arithmetic", "Barcode Scanning", "Customer Courtesy", "Punctuality"],
    entryRequirements: "100% entry-level friendly. No prior experience required.",
    sampleResponsibilities: [
      "Scan item barcodes quickly and accurately at the checkout register",
      "Accept cash, credit card, and digital mobile payments, returning exact change",
      "Issue receipts, apply coupons, and process customer refunds or exchanges",
      "Maintain a clean, organized register counter and bag customer purchases"
    ],
    interviewTips: [
      "Highlight basic math skills, trustworthiness, and attention to detail",
      "Mention willingness to stand during shifts and work flexible hours",
      "Emphasize a welcoming smile and polite conversational skills"
    ],
    outreachTip: "Ask to speak with Store Management during non-peak shopping hours (early mornings or mid-afternoons).",
    relatedCategorySlugs: ["retail-jobs", "grocery-store-jobs", "crew-member-jobs", "first-jobs"]
  },
  {
    slug: "server-jobs",
    name: "Server Jobs",
    type: "category",
    shortDescription: "Find dining room server, waiter/waitress, and food runner positions at local eateries.",
    heroHeadline: "Restaurant Server & Waitstaff Jobs Near You",
    commonWorkplaces: ["Casual Dining Restaurants", "Local Steak houses", "Neighborhood Taverns", "Breakfast Cafes"],
    keySkills: ["Menu Memorization", "POS System Operation", "Food & Beverage Pairing", "Time Management", "Gratuity Handling"],
    entryRequirements: "Previous busser/host experience helpful, but entry-level friendly at casual restaurants.",
    sampleResponsibilities: [
      "Present menus, explain daily specials, and answer ingredient questions",
      "Enter customer food and drink orders into POS terminals accurately",
      "Serve food and beverages promptly while checking back on guest satisfaction",
      "Calculate dining bills, process payments, and maintain table hygiene"
    ],
    interviewTips: [
      "Demonstrate warmth, strong communication, and excellent memory",
      "Express ability to handle multiple table assignments simultaneously with speed and poise",
      "Confirm availability for evening and weekend dining shifts"
    ],
    outreachTip: "Visit local casual restaurants between 2:00 PM and 4:00 PM to talk to dining room managers.",
    relatedCategorySlugs: ["restaurant-jobs", "host-jobs", "bartender-jobs", "part-time-jobs"]
  },
  {
    slug: "barista-jobs",
    name: "Barista Jobs",
    type: "category",
    shortDescription: "Find espresso bar, specialty coffee shop, and cafe barista positions in your neighborhood.",
    heroHeadline: "Local Coffee Shop & Barista Jobs Near You",
    commonWorkplaces: ["Independent Coffee Shops", "Specialty Espresso Bars", "Bakery Cafes", "Bookstore Cafes"],
    keySkills: ["Espresso Extraction", "Milk Steaming", "Recipe Consistency", "Friendly Conversation", "Speed under Pressure"],
    entryRequirements: "Entry-level friendly at many cafes. On-the-job training provided for espresso equipment.",
    sampleResponsibilities: [
      "Prepare espresso drinks, teas, cold brews, and specialty beverages to order",
      "Operate commercial espresso machines, grinders, and POS systems",
      "Maintain clean coffee bar counters, sanitize steam wands, and restock supplies",
      "Engage customers warmly and offer recommendations on roast origins"
    ],
    interviewTips: [
      "Share genuine passion for coffee culture and customer interaction",
      "Highlight early morning availability (often starting at 5:30 AM or 6:00 AM)",
      "Emphasize ability to memorize complex beverage orders accurately"
    ],
    outreachTip: "Visit independent coffee shops in the early afternoon when morning coffee rushes taper off.",
    relatedCategorySlugs: ["bakery-jobs", "restaurant-jobs", "cashier-jobs", "part-time-jobs"]
  },
  {
    slug: "cleaning-jobs",
    name: "Cleaning Jobs",
    type: "category",
    shortDescription: "Explore commercial janitorial, residential housekeeping, and facility sanitation positions.",
    heroHeadline: "Cleaning, Janitorial & Housekeeping Jobs Near You",
    commonWorkplaces: ["Commercial Cleaning Services", "Hotel Housekeeping", "Medical Facility Cleaning", "School Janitorial Staff"],
    keySkills: ["Sanitation Standards", "Chemical Safety", "Time Efficiency", "Self-Motivation", "Attention to Detail"],
    entryRequirements: "Entry-level friendly. No formal degree needed.",
    sampleResponsibilities: [
      "Sweep, mop, vacuum, and scrub floors in office, retail, or clinical spaces",
      "Sanitize restrooms, replenish paper products, and empty trash receptacles",
      "Clean glass windows, dust furniture, and disinfect high-touch surfaces",
      "Operate floor buffers, carpet extractors, and industrial vacuum units"
    ],
    interviewTips: [
      "Highlight thoroughness, pride in clean environments, and physical stamina",
      "Emphasize reliability when working unsupervised or on evening/night shifts",
      "Mention knowledge of basic cleaning chemicals and safety protocols"
    ],
    outreachTip: "Contact commercial janitorial supervisors during late afternoon hours before night shifts begin.",
    relatedCategorySlugs: ["maintenance-jobs", "hotel-jobs", "night-jobs", "no-experience-jobs"]
  },
  {
    slug: "landscaping-jobs",
    name: "Landscaping Jobs",
    type: "category",
    shortDescription: "Find outdoor groundskeeping, lawn care, crew member, and seasonal landscape maintenance roles.",
    heroHeadline: "Landscaping, Groundskeeping & Outdoor Jobs",
    commonWorkplaces: ["Residential Landscaping Companies", "Commercial Groundskeeping", "Golf Courses", "Tree Care Services"],
    keySkills: ["Mower & Trimmer Operation", "Plant Care", "Physical Stamina", "Outdoor Endurance", "Teamwork"],
    entryRequirements: "No prior experience needed for crew member roles. Valid driver's license is a bonus.",
    sampleResponsibilities: [
      "Operate commercial lawn mowers, string trimmers, edgers, and leaf blowers",
      "Plant flowers, shrubs, and trees; spread mulch, topsoil, and fertilizer",
      "Prune hedges, weed garden beds, and clear yard debris",
      "Install irrigation systems, pavers, and stone landscaping features"
    ],
    interviewTips: [
      "Emphasize comfort working outdoors in all weather conditions",
      "Highlight physical strength, stamina, and reliable early morning attendance",
      "Mention experience driving trucks or pulling equipment trailers if applicable"
    ],
    outreachTip: "Contact landscape company owners around 6:30 AM before crew vehicles roll out.",
    relatedCategorySlugs: ["construction-jobs", "laborer-jobs", "seasonal-jobs", "summer-jobs"]
  },
  {
    slug: "delivery-jobs",
    name: "Delivery Jobs",
    type: "category",
    shortDescription: "Search local courier, route driver, parcel delivery, and auto parts distribution roles.",
    heroHeadline: "Local Delivery, Courier & Driver Jobs Near You",
    commonWorkplaces: ["Auto Parts Retailers", "Medical Courier Services", "Local Catering Companies", "Parcel Delivery Fleets"],
    keySkills: ["Safe Driving Record", "Route Navigation", "Punctuality", "Package Handling", "Mobile App Logging"],
    entryRequirements: "Valid driver's license and clean driving record required for vehicle delivery roles.",
    sampleResponsibilities: [
      "Load delivery vehicles securely according to planned distribution routes",
      "Transport packages, auto parts, or meals safely to business and residential destinations",
      "Obtain customer delivery signatures and update mobile tracking applications",
      "Conduct daily pre-trip vehicle safety inspections and report maintenance needs"
    ],
    interviewTips: [
      "Highlight a pristine motor vehicle record and strong local geography knowledge",
      "Emphasize commitment to vehicle safety, defensive driving, and on-time arrivals",
      "Show comfort using GPS navigation apps and digital route tracking"
    ],
    outreachTip: "Inquire with local auto parts stores or courier dispatchers during morning hours.",
    relatedCategorySlugs: ["warehouse-jobs", "automotive-jobs", "full-time-jobs", "part-time-jobs"]
  },
  {
    slug: "hotel-jobs",
    name: "Hotel Jobs",
    type: "category",
    shortDescription: "Discover front desk agent, housekeeping, valet, maintenance, and guest services roles at local hotels.",
    heroHeadline: "Local Hotel, Motel & Hospitality Jobs Near You",
    commonWorkplaces: ["Independent Boutique Hotels", "Extended Stay Properties", "Chain Hotels", "Inns & Bed & Breakfasts"],
    keySkills: ["Guest Hospitality", "Property Management Software", "Housekeeping Efficiency", "Problem Resolution"],
    entryRequirements: "Positions available at all skill levels, from entry-level housekeeping/valet to front desk coordination.",
    sampleResponsibilities: [
      "Check guests in and out, assign rooms, and process payment transactions",
      "Clean and sanitize guest rooms, change linens, and restock toiletries",
      "Respond promptly to guest inquiries, requests, and room maintenance issues",
      "Inspect hotel grounds, lobbies, and amenity areas to ensure top presentation"
    ],
    interviewTips: [
      "Highlight warm hospitality, neat appearance, and patience with traveling guests",
      "Emphasize flexibility for 1st, 2nd, or 3rd shift schedules including weekends and holidays",
      "Share any customer service experience"
    ],
    outreachTip: "Inquire at front desks between 11:00 AM and 2:00 PM (between check-out and check-in times).",
    relatedCategorySlugs: ["receptionist-jobs", "cleaning-jobs", "hospitality", "night-jobs"]
  },
  {
    slug: "manufacturing-jobs",
    name: "Manufacturing Jobs",
    type: "category",
    shortDescription: "Find assembly line, machine operator, quality inspection, and production roles at nearby plants.",
    heroHeadline: "Local Manufacturing & Production Jobs Near You",
    commonWorkplaces: ["Precision Machine Shops", "Electronics Assembly Plants", "Food Processing Facilities", "Packaging Factories"],
    keySkills: ["Machine Operation", "Quality Inspection", "Blueprint / Schematic Reading", "Safety Protocol", "Assembly"],
    entryRequirements: "Entry-level assembly roles require dexterity and basic math skills. Training provided.",
    sampleResponsibilities: [
      "Assemble components accurately according to work instructions and engineering specs",
      "Operate production machinery, stamping presses, or automated equipment",
      "Inspect finished goods visually and with calipers to ensure quality standards",
      "Package products, log output counts, and maintain clean manufacturing cells"
    ],
    interviewTips: [
      "Focus on precision, safety awareness, and manual dexterity",
      "Mention comfort standing on shop floors and wearing protective equipment (PPE)",
      "Emphasize strong attendance history"
    ],
    outreachTip: "Connect with Plant Hiring Managers or HR Specialists on Tuesday or Thursday mornings.",
    relatedCategorySlugs: ["warehouse-jobs", "maintenance-jobs", "full-time-jobs", "night-jobs"]
  },
  {
    slug: "childcare-jobs",
    name: "Childcare Jobs",
    type: "category",
    shortDescription: "Explore daycare assistant, preschool aide, after-school program, and nanny positions near you.",
    heroHeadline: "Local Childcare, Daycare & Youth Program Jobs",
    commonWorkplaces: ["Licensed Daycare Centers", "Preschool Facilities", "After-School Programs", "Community Youth Centers"],
    keySkills: ["Child Development", "Patient Supervision", "Activity Planning", "CPR & First Aid", "Parent Communication"],
    entryRequirements: "Background check required. High school diploma or CDA credential helpful.",
    sampleResponsibilities: [
      "Supervise toddlers or school-age children in safe, engaging play environments",
      "Lead educational activities, arts and crafts, outdoor games, and story times",
      "Assist young children with meals, hygiene, nap times, and social learning",
      "Communicate daily activity logs and milestone observations to parents"
    ],
    interviewTips: [
      "Demonstrate warmth, patience, energy, and strong safety vigilance",
      "Detail CPR/First Aid certifications or childcare training courses completed",
      "Share real experience supervising children in camps, babysitting, or schools"
    ],
    outreachTip: "Visit daycare centers during quiet naptime hours (1:00 PM – 2:30 PM).",
    relatedCategorySlugs: ["after-school-jobs", "teacher-aide-jobs", "first-jobs", "part-time-jobs"]
  },
  {
    slug: "pet-care-jobs",
    name: "Pet Care Jobs",
    type: "category",
    shortDescription: "Search dog daycare attendant, kennel technician, pet grooming aide, and veterinary clinic support roles.",
    heroHeadline: "Pet Care, Dog Daycare & Vet Assistant Jobs Near You",
    commonWorkplaces: ["Dog Daycares & Boarding Kennels", "Veterinary Clinics", "Pet Grooming Salons", "Local Animal Shelters"],
    keySkills: ["Animal Handling", "Facility Sanitation", "Pet Behavior Awareness", "Physical Stamina", "Client Courtesy"],
    entryRequirements: "Entry-level friendly for kennel attendants and dog daycare monitors. On-the-job training provided.",
    sampleResponsibilities: [
      "Monitor group play areas in dog daycares to ensure safe pet interactions",
      "Feed animals according to dietary instructions and administer medication",
      "Clean kennels, sanitize play yards, wash pet bedding, and bathe animals",
      "Greet pet owners, check animals in/out, and maintain client logs"
    ],
    interviewTips: [
      "Highlight calm confidence around animals of all breeds and sizes",
      "Emphasize willingness to clean kennels and perform hard physical work outdoors",
      "Mention personal experience with pets or animal volunteering"
    ],
    outreachTip: "Stop by local boarding kennels or vet offices during mid-day non-peak dropoff hours.",
    relatedCategorySlugs: ["cleaning-jobs", "first-jobs", "weekend-jobs", "part-time-jobs"]
  },
  {
    slug: "sales-jobs",
    name: "Sales Jobs",
    type: "category",
    shortDescription: "Find inside sales, retail commission sales, B2B account representative, and outreach specialist roles.",
    heroHeadline: "Local B2B & Retail Sales Positions Near You",
    commonWorkplaces: ["Auto Dealerships", "Insurance Agencies", "Building Material Suppliers", "B2B Service Providers"],
    keySkills: ["Lead Prospecting", "Relationship Building", "Negotiation", "CRM Management", "Closing"],
    entryRequirements: "Roles range from entry-level sales trainees to high-commission B2B specialists.",
    sampleResponsibilities: [
      "Identify potential sales prospects, make outbound calls, and send pitch emails",
      "Conduct product demonstrations and present solution quotes to clients",
      "Follow up with prospective customers and address objections constructively",
      "Maintain prospective deal pipelines in CRM software and achieve sales targets"
    ],
    interviewTips: [
      "Demonstrate high energy, resilience, persuasiveness, and goal-driven focus",
      "Share examples of overcoming obstacles or achieving performance targets",
      "Ask insightful questions about commission structures and lead generation support"
    ],
    outreachTip: "Reach out to Sales Managers directly via email or phone on Monday or Tuesday morning.",
    relatedCategorySlugs: ["customer-service-jobs", "office-jobs", "full-time-jobs", "entry-level-jobs"]
  },
  {
    slug: "administrative-jobs",
    name: "Administrative Jobs",
    type: "category",
    shortDescription: "Search administrative assistant, executive secretary, office coordinator, and billing clerk positions.",
    heroHeadline: "Administrative Assistant & Office Support Jobs Near You",
    commonWorkplaces: ["Local Law Practices", "Accounting Offices", "Construction Firms", "Medical Offices"],
    keySkills: ["Calendar Management", "Spreadsheet Proficiency", "Document Preparation", "Communication", "Filing"],
    entryRequirements: "High school diploma or Associate's degree. Proficiency in MS Office/Google Suite.",
    sampleResponsibilities: [
      "Prepare correspondence, reports, billing invoices, and meeting slide decks",
      "Maintain executive calendars, schedule appointments, and coordinate travel",
      "Manage office records, organize physical and digital filing structures",
      "Order office supplies, coordinate vendor maintenance, and assist team projects"
    ],
    interviewTips: [
      "Highlight organizational systems, software mastery, and high accuracy",
      "Bring formatted samples of professional documents or writing if applicable",
      "Emphasize discretion and confidentiality when handling sensitive company files"
    ],
    outreachTip: "Contact Office Directors directly during mid-morning working hours.",
    relatedCategorySlugs: ["office-jobs", "receptionist-jobs", "customer-service-jobs", "full-time-jobs"]
  },
  {
    slug: "automotive-jobs",
    name: "Automotive Jobs",
    type: "category",
    shortDescription: "Discover shop helper, lube technician, tire installer, auto detailer, and service advisor roles.",
    heroHeadline: "Automotive Repair, Detailing & Service Jobs Near You",
    commonWorkplaces: ["Independent Auto Repair Shops", "Tire & Brake Centers", "Car Dealerships", "Auto Detailing Studios"],
    keySkills: ["Vehicle Maintenance", "Diagnostic Tools", "Mechanical Aptitude", "Safety Protocol", "Physical Stamina"],
    entryRequirements: "Shop helper, tire tech, and detailer roles require no formal ASE certification. Apprenticeships common.",
    sampleResponsibilities: [
      "Perform oil changes, tire rotations, fluid flushes, and routine maintenance",
      "Inspect brakes, suspension, belts, and batteries for wear",
      "Wash, vacuum, polish, and detail customer vehicles thoroughly",
      "Maintain clean garage bays, dispose of fluids safely, and organize tools"
    ],
    interviewTips: [
      "Demonstrate mechanical curiosity, hands-on aptitude, and strong safety discipline",
      "Bring up reliable attendance and personal toolsets owned if applicable",
      "Express eagerness to work toward ASE technician certifications"
    ],
    outreachTip: "Visit shop owners or service directors around 8:00 AM as morning repair check-ins conclude.",
    relatedCategorySlugs: ["maintenance-jobs", "delivery-jobs", "laborer-jobs", "full-time-jobs"]
  },
  {
    slug: "security-jobs",
    name: "Security Jobs",
    type: "category",
    shortDescription: "Find security guard, building monitor, event security, and loss prevention positions.",
    heroHeadline: "Local Security Guard & Building Monitoring Jobs",
    commonWorkplaces: ["Commercial Office Parks", "Shopping Malls", "Residential Complexes", "Event Venues"],
    keySkills: ["Surveillance Monitoring", "Incident Reporting", "Access Control", "Conflict De-escalation", "Vigilance"],
    entryRequirements: "State security guard license or registration often required (or assistance provided upon hire).",
    sampleResponsibilities: [
      "Patrol commercial or residential property grounds on foot or by vehicle",
      "Monitor security cameras, alarm panels, and building access control gates",
      "Greet visitors, verify ID credentials, and maintain visitor logbooks",
      "Write detailed incident reports and notify emergency services when needed"
    ],
    interviewTips: [
      "Emphasize integrity, sharp observational skills, calm authority, and punctuality",
      "Highlight clear written communication for incident reports",
      "Confirm availability for overnight, weekend, or holiday shifts"
    ],
    outreachTip: "Contact regional security firm branch managers directly via phone or web application.",
    relatedCategorySlugs: ["night-jobs", "full-time-jobs", "cleaning-jobs", "entry-level-jobs"]
  },
  {
    slug: "maintenance-jobs",
    name: "Maintenance Jobs",
    type: "category",
    shortDescription: "Explore facility maintenance, apartment technician, building engineer, and repair specialist roles.",
    heroHeadline: "Facility Maintenance & Building Technician Jobs",
    commonWorkplaces: ["Apartment Communities", "Commercial Property Management", "Schools & Colleges", "Hospitality Resorts"],
    keySkills: ["Basic Plumbing & Electrical", "HVAC Maintenance", "Drywall & Painting", "Appliance Repair", "Troubleshooting"],
    entryRequirements: "Maintenance helper roles require general hand tool experience. Tech roles require trade experience.",
    sampleResponsibilities: [
      "Respond to tenant or facility maintenance service requests promptly",
      "Repair minor plumbing leaks, clear drains, replace light fixtures and outlets",
      "Perform drywall patching, interior painting, and door lock replacements",
      "Maintain heating, ventilation, air conditioning (HVAC) and pool systems"
    ],
    interviewTips: [
      "Share hands-on problem solving examples across plumbing, electric, or drywall",
      "Highlight emergency on-call availability and reliable transportation",
      "Mention EPA universal certifications or trade licenses if held"
    ],
    outreachTip: "Visit apartment community leasing offices during early morning hours to speak with Property Managers.",
    relatedCategorySlugs: ["construction-jobs", "cleaning-jobs", "landscaping-jobs", "full-time-jobs"]
  },
  {
    slug: "stocking-jobs",
    name: "Stocking Jobs",
    type: "category",
    shortDescription: "Find night stocker, inventory clerk, shelf replenishment, and receiving associate positions.",
    heroHeadline: "Shelf Stocker, Inventory & Receiving Jobs Near You",
    commonWorkplaces: ["Big-Box Stores", "Supermarkets", "Hardware Centers", "Distribution Warehouses"],
    keySkills: ["Shelf Replenishment", "Pallet Jack Operation", "Inventory Auditing", "Heavy Lifting", "Organization"],
    entryRequirements: "100% entry-level friendly. No prior experience required.",
    sampleResponsibilities: [
      "Unload delivery trucks using pallet jacks and transport freight to sales floor",
      "Unpack boxes, stock shelves neatly according to store planograms, and rotate stock",
      "Break down cardboard boxes, operate compactors, and clear aisles",
      "Verify pricing labels and conduct inventory counts"
    ],
    interviewTips: [
      "Highlight physical stamina, efficiency, and comfort with nighttime or early morning hours",
      "Emphasize attention to detail when arranging products facing forward neatly",
      "Express commitment to workplace safety"
    ],
    outreachTip: "Inquire about overnight or early morning stocking teams with Store Managers.",
    relatedCategorySlugs: ["grocery-store-jobs", "warehouse-jobs", "night-jobs", "first-jobs"]
  },
  {
    slug: "kitchen-jobs",
    name: "Kitchen Jobs",
    type: "category",
    shortDescription: "Explore prep cook, line cook, kitchen utility, and food prep positions at local food establishments.",
    heroHeadline: "Kitchen, Line Cook & Food Prep Positions Near You",
    commonWorkplaces: ["Local Restaurants", "Catering Kitchens", "Hotel Banquets", "Hospital Food Services"],
    keySkills: ["Knife Handling Skills", "Recipe Execution", "Food Sanitation", "High-Volume Cooking", "Kitchen Speed"],
    entryRequirements: "Prep cook & utility roles available entry-level. Line cook roles require basic culinary technique.",
    sampleResponsibilities: [
      "Chop vegetables, slice meats, prepare sauces, and portion recipe ingredients",
      "Cook food items on grills, fryers, sautoirs, and ovens to exact ticket specs",
      "Maintain clean cooking stations, sanitize food contact surfaces, and label items",
      "Restock line coolers and organize walk-in refrigerators"
    ],
    interviewTips: [
      "Demonstrate knife skills, food safety knowledge, and passion for cooking",
      "Emphasize speed, composure during dinner rushes, and teamwork",
      "Bring non-slip shoes and chef's attire if asked to complete a practical staging shift"
    ],
    outreachTip: "Ask to speak to the Executive Chef or Kitchen Manager weekday afternoons between 2:00 PM and 4:00 PM.",
    relatedCategorySlugs: ["cook-jobs", "dishwasher-jobs", "restaurant-jobs", "fast-food-jobs"]
  },
  {
    slug: "host-jobs",
    name: "Host Jobs",
    type: "category",
    shortDescription: "Find dining room host/hostess, greeter, and reservation coordinator positions at local restaurants.",
    heroHeadline: "Restaurant Host & Dining Room Greeter Jobs",
    commonWorkplaces: ["Casual & Fine Dining Restaurants", "Bistro Cafes", "Event Centers", "Steakhouses"],
    keySkills: ["Warm Reception", "Table Rotation Management", "Reservation Software", "Poise under Pressure", "Punctuality"],
    entryRequirements: "100% entry-level friendly. Excellent stepping stone into restaurant serving.",
    sampleResponsibilities: [
      "Welcome guests enthusiastically upon arrival and manage guest waiting lists",
      "Escort parties to dining tables, distribute menus, and inform guests of server names",
      "Balance server table rotations fairly across the dining room floor",
      "Answer incoming phone calls, take reservations, and manage curbside pickup orders"
    ],
    interviewTips: [
      "Present a warm, welcoming demeanor and clear verbal communication",
      "Demonstrate ability to manage chaotic waiting crowds with poise and fairness",
      "Highlight enthusiasm for hospitality"
    ],
    outreachTip: "Inquire with restaurant managers weekday afternoons before dinner service begins.",
    relatedCategorySlugs: ["server-jobs", "restaurant-jobs", "first-jobs", "teen-jobs"]
  },
  {
    slug: "dishwasher-jobs",
    name: "Dishwasher Jobs",
    type: "category",
    shortDescription: "Find kitchen utility, dish machine operator, and sanitation associate roles at local eateries.",
    heroHeadline: "Kitchen Dishwasher & Utility Jobs Near You",
    commonWorkplaces: ["Restaurants", "Cafeterias", "Catering Halls", "Hospital Kitchens"],
    keySkills: ["Dish Machine Operation", "Kitchen Sanitation", "Speed & Efficiency", "Heavy Lifting", "Reliability"],
    entryRequirements: "100% entry-level friendly. No prior experience or education needed.",
    sampleResponsibilities: [
      "Load, run, and unload commercial dishwashing machines",
      "Scrub pots, pans, and cooking utensils by hand using multi-compartment sinks",
      "Organize clean dishware, glassware, and cookware back to station lines",
      "Take out kitchen trash, mop kitchen floors, and assist prep cooks as needed"
    ],
    interviewTips: [
      "Stress rock-solid attendance, strong work ethic, and speed",
      "Express pride in keeping a clean, organized kitchen backbone",
      "Mention willingness to learn prep cooking skills over time"
    ],
    outreachTip: "Walk into local restaurant kitchens mid-afternoon and ask for the Kitchen Manager.",
    relatedCategorySlugs: ["kitchen-jobs", "restaurant-jobs", "first-jobs", "no-experience-jobs"]
  },
  {
    slug: "crew-member-jobs",
    name: "Crew Member Jobs",
    type: "category",
    shortDescription: "Explore team crew member positions at quick-service eateries, smoothie shops, and retail chains.",
    heroHeadline: "Store & Restaurant Crew Member Jobs Near You",
    commonWorkplaces: ["Fast-Casual Chains", "Ice Cream Shops", "Bakeries", "Drive-Thru Restaurants"],
    keySkills: ["Teamwork", "Customer Greeting", "Order Fulfillment", "Cleaning & Sanitizing", "Flexibility"],
    entryRequirements: "100% entry-level friendly. Excellent first job for teenagers and students.",
    sampleResponsibilities: [
      "Rotate through register, food assembly, drive-thru, and cleaning stations",
      "Prepare food items, assemble customer orders, and hand over orders warmly",
      "Clean dining tables, sweep floors, and sanitize food preparation counters",
      "Restock cups, napkins, condiments, and beverage syrup dispensers"
    ],
    interviewTips: [
      "Show enthusiastic energy, positive attitude, and willingness to work hard",
      "Emphasize flexible availability for evening or weekend shifts",
      "Share group activity or school club teamwork experience"
    ],
    outreachTip: "Apply in person or ask for shift managers mid-mornings on weekdays.",
    relatedCategorySlugs: ["fast-food-jobs", "cashier-jobs", "first-jobs", "teen-jobs"]
  },
  {
    slug: "cook-jobs",
    name: "Cook Jobs",
    type: "category",
    shortDescription: "Find line cook, grill cook, fry cook, and short-order cook positions across local dining rooms.",
    heroHeadline: "Line Cook, Grill Cook & Prep Cook Jobs Near You",
    commonWorkplaces: ["Independent Diners", "Pubs & Grills", "Italian Eateries", "Breakfast Cafes"],
    keySkills: ["Grill & Line Technique", "Ticket Timing", "Food Temperature Safety", "Recipe Execution", "Composure"],
    entryRequirements: "Line cook roles usually require 6-12 months kitchen experience or culinary training.",
    sampleResponsibilities: [
      "Cook meat, seafood, poultry, and vegetables according to recipe standards",
      "Manage station order tickets during high-volume meal surges",
      "Maintain clean cooking equipment, oil fryers, and station coolers",
      "Adhere to food temperature storage and cross-contamination prevention rules"
    ],
    interviewTips: [
      "Focus on cooking speed, accuracy, ticket timing, and sanitation standards",
      "Highlight ability to handle 20+ active tickets without losing composure",
      "Be prepared for a short cooking staging test during the interview process"
    ],
    outreachTip: "Visit kitchen managers directly between 2:00 PM and 4:00 PM.",
    relatedCategorySlugs: ["kitchen-jobs", "restaurant-jobs", "fast-food-jobs", "full-time-jobs"]
  },
  {
    slug: "bakery-jobs",
    name: "Bakery Jobs",
    type: "category",
    shortDescription: "Discover baker's assistant, cake decorator, pastry helper, and bakery counter associate positions.",
    heroHeadline: "Local Bakery, Pastry & Donut Shop Jobs Near You",
    commonWorkplaces: ["Independent Retail Bakeries", "Grocery Store Bakeries", "Donut Shops", "Artisan Bread Houses"],
    keySkills: ["Baking Measurement Accuracy", "Dough Handling", "Cake Decorating", "Counter Service", "Early Morning Attendance"],
    entryRequirements: "Counter & helper roles require no experience. Lead bakers require commercial baking experience.",
    sampleResponsibilities: [
      "Scale ingredients accurately, mix doughs, shape pastries, and operate commercial ovens",
      "Decorate cakes, cookies, and pastries with piping bags and icing",
      "Package baked goods neatly into boxes and arrange display cases attractively",
      "Assist bakery customers, slice breads, and take custom cake orders"
    ],
    interviewTips: [
      "Emphasize extreme reliability for early morning baking shifts (often 3:00 AM – 5:00 AM start)",
      "Highlight measurement precision, attention to recipe instructions, and cleanliness",
      "Bring photos of cakes, pastries, or home baking projects if decorating"
    ],
    outreachTip: "Inquire at local bakeries during late morning hours after early customer rushes conclude.",
    relatedCategorySlugs: ["barista-jobs", "grocery-store-jobs", "cashier-jobs", "part-time-jobs"]
  },

  // --- EMPLOYMENT TYPES & SPECIAL INTENTS ---
  {
    slug: "part-time-jobs",
    name: "Part-Time Jobs",
    type: "employment-type",
    shortDescription: "Explore local part-time positions offering flexible 10-30 hour weekly schedules near you.",
    heroHeadline: "Find Local Part-Time Jobs & Flexible Hours Near You",
    commonWorkplaces: ["Retail Stores", "Restaurants & Cafes", "Grocery Stores", "Tutoring Centers", "Fitness Clubs"],
    keySkills: ["Time Management", "Schedule Flexibility", "Customer Service", "Punctuality", "Adaptability"],
    entryRequirements: "Available at all skill and experience levels across retail, food, admin, and healthcare.",
    sampleResponsibilities: [
      "Fulfill shift duties reliably during scheduled 4-8 hour daily shifts",
      "Maintain open communication with managers regarding school or family schedule availability",
      "Deliver consistent customer service during peak operational hours"
    ],
    interviewTips: [
      "Be completely upfront and clear about your available working days and hours",
      "Stress punctuality and willingness to cover open shifts when coworkers are sick",
      "Highlight quick learning speed"
    ],
    outreachTip: "Look for businesses displaying window hiring signs or ask shift supervisors in person.",
    relatedCategorySlugs: ["retail-jobs", "restaurant-jobs", "cashier-jobs", "weekend-jobs"]
  },
  {
    slug: "full-time-jobs",
    name: "Full-Time Jobs",
    type: "employment-type",
    shortDescription: "Find 40-hour weekly full-time positions with competitive pay, benefits, and local growth potential.",
    heroHeadline: "Full-Time Career Positions & Local Employer Openings",
    commonWorkplaces: ["Offices & Agencies", "Warehouses & Factories", "Trade Contractors", "Medical Clinics"],
    keySkills: ["Core Technical Skills", "Reliability", "Professionalism", "Project Completion", "Team Alignment"],
    entryRequirements: "Varies from entry-level full-time roles to experienced technical specialist positions.",
    sampleResponsibilities: [
      "Execute daily operational duties across 40-hour weekly work schedules",
      "Collaborate with team members on ongoing projects and operational targets",
      "Participate in company meetings, skill trainings, and performance reviews"
    ],
    interviewTips: [
      "Demonstrate long-term commitment and desire to grow with the company",
      "Highlight past achievements, reliable attendance history, and teamwork",
      "Inquire thoughtfully about company culture and development opportunities"
    ],
    outreachTip: "Connect with Hiring Managers directly or apply through local employer career portals.",
    relatedCategorySlugs: ["warehouse-jobs", "office-jobs", "construction-jobs", "healthcare-jobs"]
  },
  {
    slug: "entry-level-jobs",
    name: "Entry-Level Jobs",
    type: "employment-type",
    shortDescription: "Discover entry-level roles offering on-the-job training without requiring years of prior experience.",
    heroHeadline: "Entry-Level Jobs & Paid On-The-Job Training Roles",
    commonWorkplaces: ["Local Businesses", "Customer Service Centers", "Medical Practices", "Distribution Centers"],
    keySkills: ["Willingness to Learn", "Strong Work Ethic", "Positive Attitude", "Basic Computer Literacy", "Reliability"],
    entryRequirements: "No specialized prior experience required. High school diploma or equivalent usually sufficient.",
    sampleResponsibilities: [
      "Complete company orientation and job skill training programs",
      "Assist senior staff members while learning foundational workflow procedures",
      "Execute routine tasks accurately and ask questions when guidance is needed"
    ],
    interviewTips: [
      "Highlight enthusiasm, coachability, and eagerness to learn new systems",
      "Draw on school projects, volunteer activities, or sports to demonstrate leadership and grit",
      "Ask intelligent questions about training structures"
    ],
    outreachTip: "Emphasize your strong work ethic when reaching out to local business owners directly.",
    relatedCategorySlugs: ["first-jobs", "no-experience-jobs", "retail-jobs", "receptionist-jobs"]
  },
  {
    slug: "no-experience-jobs",
    name: "No Experience Jobs",
    type: "employment-type",
    shortDescription: "Search positions requiring zero prior work experience. Get hired based on attitude and reliability.",
    heroHeadline: "Jobs Requiring No Prior Work Experience Near You",
    commonWorkplaces: ["Fast Food & Diners", "Grocery Supermarkets", "Cleaning Services", "Landscaping Crews"],
    keySkills: ["Reliability", "Coachable Attitude", "Punctuality", "Basic Communication", "Energy"],
    entryRequirements: "Zero experience necessary. Employers train you from scratch on day one.",
    sampleResponsibilities: [
      "Follow supervisor instructions carefully and learn daily task routines",
      "Maintain clean, safe, and organized work areas",
      "Provide polite assistance to customers or team members"
    ],
    interviewTips: [
      "Focus on personality traits: reliability, hard work, positivity, and promptness",
      "Dress neatly and make confident eye contact",
      "Confirm readiness to start work immediately"
    ],
    outreachTip: "Inquire in person at local small businesses where owners hire based on personal trust.",
    relatedCategorySlugs: ["first-jobs", "entry-level-jobs", "teen-jobs", "dishwasher-jobs"]
  },
  {
    slug: "first-jobs",
    name: "First Jobs",
    type: "employment-type",
    shortDescription: "Find beginner-friendly first jobs designed for first-time workers entering the workforce.",
    heroHeadline: "First Jobs for Beginners & First-Time Workers",
    commonWorkplaces: ["Local Bakeries & Cafes", "Retail Shops", "Ice Cream Parlors", "Pet Care Centers"],
    keySkills: ["Basic Communication", "Following Directions", "Timekeeping", "Friendly Demeanor", "Teamwork"],
    entryRequirements: "Perfect for young adults, teenagers, and students seeking their very first resume builder.",
    sampleResponsibilities: [
      "Perform simple customer service, cleaning, or stocking tasks under supervision",
      "Operate cash register terminals or assist with order packing",
      "Learn essential workplace habits like clocking in on time and professional communication"
    ],
    interviewTips: [
      "Express excitement about starting your work history",
      "Mention extracurricular activities, school attendance records, or household responsibilities",
      "Practice basic interview answers with a friend or family member beforehand"
    ],
    outreachTip: "Visit neighborhood ice cream shops, bakeries, or retail stores with a simple 1-page resume.",
    relatedCategorySlugs: ["teen-jobs", "no-experience-jobs", "summer-jobs", "crew-member-jobs"]
  },
  {
    slug: "teen-jobs",
    name: "Teen Jobs",
    type: "student",
    shortDescription: "Explore local jobs for teenagers (ages 14-19) featuring flexible evening and weekend shifts.",
    heroHeadline: "Teen Jobs & Flexible High School Work Near You",
    commonWorkplaces: ["Fast-Casual Restaurants", "Movie Theaters", "Grocery Stores", "Amusement & Recreation Centers"],
    keySkills: ["Balancing School & Work", "Customer Courtesy", "Task Efficiency", "Team Spirit", "Punctuality"],
    entryRequirements: "Ages 14-19. Work permit may be required for youth under 16 depending on state regulations.",
    sampleResponsibilities: [
      "Assist customers, operate cash registers, and scoop ice cream or serve food",
      "Stock shelves, bag groceries, and maintain clean store facilities",
      "Follow work permit hour limits during school weeks"
    ],
    interviewTips: [
      "Bring a completed work permit (if required by your state for under-16 hiring)",
      "Confirm that work schedules will fit smoothly around school hours and homework",
      "Show a mature, polite demeanor during the manager conversation"
    ],
    outreachTip: "Visit local businesses in late spring for summer hiring, or late summer for back-to-school hiring.",
    relatedCategorySlugs: ["first-jobs", "after-school-jobs", "summer-jobs", "cashier-jobs"]
  },
  {
    slug: "summer-jobs",
    name: "Summer Jobs",
    type: "student",
    shortDescription: "Find temporary summer positions at seasonal attractions, camps, pools, landscaping crews, and resorts.",
    heroHeadline: "Summer Jobs & Seasonal Work Opportunities Near You",
    commonWorkplaces: ["Summer Youth Camps", "Community Swimming Pools", "Landscaping Companies", "Seasonal Tourist Spots"],
    keySkills: ["High Energy", "Outdoor Fitness", "Team Collaboration", "Customer Hospitality", "Adaptability"],
    entryRequirements: "Great for high school students, college students, and teachers during summer break.",
    sampleResponsibilities: [
      "Staff seasonal summer operations from May through August",
      "Lead recreational games, lifeguard swimming pools, or maintain outdoor grounds",
      "Provide friendly seasonal guest services during peak tourist months"
    ],
    interviewTips: [
      "Confirm exact start and end dates of summer availability upfront",
      "Highlight certifications like Lifeguard, CPR, or First Aid if applicable",
      "Demonstrate outdoor enthusiasm and teamwork"
    ],
    outreachTip: "Apply early in March and April before summer seasonal slots fill up.",
    relatedCategorySlugs: ["teen-jobs", "landscaping-jobs", "seasonal-jobs", "internships"]
  },
  {
    slug: "weekend-jobs",
    name: "Weekend Jobs",
    type: "employment-type",
    shortDescription: "Search Saturday and Sunday weekend shifts ideal for students, second income earners, and busy schedules.",
    heroHeadline: "Weekend Jobs (Saturday & Sunday Shifts) Near You",
    commonWorkplaces: ["Event Catering Companies", "Retail Outlets", "Restaurants & Bars", "Dog Daycares"],
    keySkills: ["Weekend Reliability", "Customer Service", "Punctuality", "Energy during Busy Hours"],
    entryRequirements: "Flexible entry-level to experienced shifts available strictly on weekends.",
    sampleResponsibilities: [
      "Staff busy weekend retail surges, restaurant meal shifts, or catered wedding events",
      "Execute store opening or closing procedures on Saturday and Sunday",
      "Provide high-energy customer assistance during peak weekend customer foot traffic"
    ],
    interviewTips: [
      "Stress 100% reliable availability for Saturdays and Sundays without constant shift swaps",
      "Express enthusiasm for working peak busy hours",
      "Highlight efficiency under high customer volume"
    ],
    outreachTip: "Inquire directly with weekend managers at retail stores and event venues.",
    relatedCategorySlugs: ["part-time-jobs", "server-jobs", "retail-jobs", "after-school-jobs"]
  },
  {
    slug: "after-school-jobs",
    name: "After-School Jobs",
    type: "student",
    shortDescription: "Find weekday afternoon and evening shifts (3:30 PM – 8:00 PM) designed around high school schedules.",
    heroHeadline: "After-School Weekday Afternoon Jobs for High Schoolers",
    commonWorkplaces: ["Tutoring Centers", "Youth Sports Facilities", "Local Cafes", "Neighborhood Retail Shops"],
    keySkills: ["Punctuality after School", "Energy", "Customer Courtesy", "Task Focus"],
    entryRequirements: "Ideal for high school students seeking 10-15 hours per week after 3:30 PM.",
    sampleResponsibilities: [
      "Clock in after school dismissal and perform store prep or customer service duties",
      "Tutor younger children or assist with youth sports coaching",
      "Restock store inventory and clean retail floors before closing"
    ],
    interviewTips: [
      "Provide your exact daily school dismissal time and transit time to the job site",
      "Assure the employer that school academics remain balanced with work",
      "Show a cheerful, mature attitude"
    ],
    outreachTip: "Reach out to local businesses located close to high school campuses.",
    relatedCategorySlugs: ["teen-jobs", "first-jobs", "childcare-jobs", "part-time-jobs"]
  },
  {
    slug: "internships",
    name: "Internships",
    type: "student",
    shortDescription: "Discover student internship opportunities at independent local companies, medical clinics, law firms, and agencies.",
    heroHeadline: "Local Student Internships & Career Learning Opportunities",
    commonWorkplaces: ["Local Tech Agencies", "Law Practices", "Medical Research Clinics", "Marketing Agencies"],
    keySkills: ["Project Research", "Software Proficiency", "Critical Thinking", "Written Communication", "Initiative"],
    entryRequirements: "High school upperclassmen or college students seeking career-relevant experience.",
    sampleResponsibilities: [
      "Support professional teams with research, documentation, social media, or project tasks",
      "Shadow experienced mentors during client meetings, court filings, or clinical rounds",
      "Complete a capstone learning project or present findings to management"
    ],
    interviewTips: [
      "Articulate clearly why you want to learn this specific industry field",
      "Highlight coursework, class projects, or self-taught skills relevant to the role",
      "Ask thoughtful questions about mentorship and day-to-day intern responsibilities"
    ],
    outreachTip: "Send a polite intro email and resume directly to small business owners explaining your internship goals.",
    relatedCategorySlugs: ["entry-level-jobs", "office-jobs", "summer-jobs", "medical-office-jobs"]
  },

  // --- PROFESSIONAL & SUGGESTED SEARCH CATEGORIES ---
  {
    slug: "medical-healthcare",
    name: "Medical & Healthcare",
    type: "category",
    shortDescription: "Discover employment and operational roles at local primary care clinics, medical practices, urgent care centers, and diagnostic labs.",
    heroHeadline: "Medical & Healthcare Offices and Employer Roles Near You",
    commonWorkplaces: ["Primary Care Clinics", "Urgent Care Centers", "Specialty Medical Practices", "Diagnostic & Imaging Labs", "Allied Health Centers"],
    keySkills: ["Patient Care", "HIPAA Compliance", "Medical Terminology", "Front Desk Scheduling", "EHR Systems"],
    entryRequirements: "Roles for both certified medical staff (MA, CNA, Phlebotomist) and entry-level support staff.",
    sampleResponsibilities: [
      "Check in incoming patients and verify insurance records",
      "Assist healthcare providers with routine clinical procedures",
      "Manage medical appointment schedules and patient communications",
      "Maintain sterile, compliant clinical environments"
    ],
    interviewTips: [
      "Demonstrate compassion, attention to detail, and awareness of patient privacy laws",
      "Highlight experience with medical scheduling or health record software"
    ],
    outreachTip: "Reach out to practice managers on mid-week afternoons when patient check-in rushes taper off.",
    relatedCategorySlugs: ["dental-clinics", "receptionist-jobs", "office-jobs", "customer-service-jobs"]
  },
  {
    slug: "dental-clinics",
    name: "Dental Clinics",
    type: "category",
    shortDescription: "Explore opportunities at neighborhood dental practices, family dentistry offices, and orthodontics clinics.",
    heroHeadline: "Dental Clinic & Practice Positions Near You",
    commonWorkplaces: ["General Dentistry Practices", "Pediatric Dental Offices", "Orthodontic Clinics", "Oral Surgery Centers"],
    keySkills: ["Patient Scheduling", "Dental Assisting", "Sterilization Protocols", "Dental Billing / Claims", "Chairside Support"],
    entryRequirements: "Roles available for licensed dental hygienists/assistants as well as entry-level front desk receptionists.",
    sampleResponsibilities: [
      "Welcome patients and manage dental appointment calendars",
      "Prepare treatment rooms and sterilize dental equipment",
      "Process dental insurance claims and handle patient co-pays",
      "Assist dentists during cleanings, x-rays, and procedures"
    ],
    interviewTips: [
      "Show a friendly, welcoming demeanor that puts anxious patients at ease",
      "Mention familiarity with dental software (Dentrix, Eaglesoft) or front desk experience"
    ],
    outreachTip: "Contact office managers on Tuesday morning or Thursday afternoon.",
    relatedCategorySlugs: ["medical-healthcare", "receptionist-jobs", "office-jobs", "customer-service-jobs"]
  },
  {
    slug: "plumbing-trades",
    name: "Plumbing & Trades",
    type: "category",
    shortDescription: "Find trade roles and apprenticeships at local plumbing, electrical, HVAC, and commercial contracting companies.",
    heroHeadline: "Plumbing, Electrical & Trade Contractor Roles Near You",
    commonWorkplaces: ["Plumbing Companies", "Electrical Contracting Firms", "HVAC Service Providers", "General Contracting Crews"],
    keySkills: ["Trade Tool Operations", "Blueprint Reading", "Physical Stamina", "Safety Compliance", "Troubleshooting"],
    entryRequirements: "Apprentice and helper positions available with zero prior trade experience; journeyman roles require licensure.",
    sampleResponsibilities: [
      "Assist lead technicians on commercial and residential service calls",
      "Transport tools, prep job sites, and maintain safety equipment",
      "Perform routine maintenance, pipe fitting, or electrical wiring tasks",
      "Interact courteously with property owners and project managers"
    ],
    interviewTips: [
      "Highlight strong work ethic, physical endurance, punctuality, and commitment to safety",
      "Bring up any mechanical aptitude, hand tool familiarity, or trade school coursework"
    ],
    outreachTip: "Reach out to shop dispatchers or site superintendents early in the morning before crews head out.",
    relatedCategorySlugs: ["construction-jobs", "maintenance-jobs", "laborer-jobs", "full-time-jobs"]
  },
  {
    slug: "legal-accounting",
    name: "Legal & Accounting",
    type: "category",
    shortDescription: "Discover administrative, paralegal, tax, and office management roles at local law firms and accounting agencies.",
    heroHeadline: "Legal & Accounting Firm Careers Near You",
    commonWorkplaces: ["Local Law Practices", "CPA & Tax Preparation Firms", "Bookkeeping Services", "Estate Planning Offices"],
    keySkills: ["Document Formatting", "QuickBooks / Tax Software", "Client Confidentiality", "Legal Research", "Calendar Management"],
    entryRequirements: "Entry-level administrative roles require strong organization and computer literacy; paralegal/CPA roles require relevant credentials.",
    sampleResponsibilities: [
      "Greet visiting clients and manage confidential firm correspondence",
      "Format legal filings, contract drafts, or client tax returns accurately",
      "Organize financial ledgers, process client invoicing, and reconcile accounts",
      "Schedule attorney and CPA consultations with clients"
    ],
    interviewTips: [
      "Emphasize precise attention to detail, discretion, and professional written communication",
      "Mention typing speed and proficiency with office/accounting software"
    ],
    outreachTip: "Send professional intro emails to managing partners or firm directors mid-morning.",
    relatedCategorySlugs: ["office-jobs", "administrative-jobs", "receptionist-jobs", "full-time-jobs"]
  },
  {
    slug: "tech-agencies",
    name: "Tech Agencies",
    type: "category",
    shortDescription: "Explore career and junior roles at neighborhood IT service providers, web development shops, and digital marketing agencies.",
    heroHeadline: "Tech Agencies, IT Services & Digital Studios Near You",
    commonWorkplaces: ["Managed IT Service Providers (MSPs)", "Web Development Studios", "Digital Marketing Agencies", "Software Consulting Shops"],
    keySkills: ["Technical Support", "Client Communication", "Web Technologies", "Problem Solving", "Project Coordination"],
    entryRequirements: "Junior support, account coordination, and entry-level developer roles available.",
    sampleResponsibilities: [
      "Provide helpdesk IT support for small business clients",
      "Coordinate client website updates, content publishing, and QA testing",
      "Assist project managers with task tracking and client status reports",
      "Monitor network systems or digital marketing campaign metrics"
    ],
    interviewTips: [
      "Demonstrate technical curiosity, quick learning ability, and problem-solving drive",
      "Share a portfolio, GitHub link, or examples of personal/class tech projects"
    ],
    outreachTip: "Reach out to agency founders or operations directors via LinkedIn or professional email.",
    relatedCategorySlugs: ["office-jobs", "internships", "customer-service-jobs", "administrative-jobs"]
  },
  {
    slug: "retail-hospitality",
    name: "Retail & Hospitality",
    type: "category",
    shortDescription: "Search store, lodging, food service, and guest experience positions at local retail shops, boutique hotels, and eateries.",
    heroHeadline: "Retail & Hospitality Roles Near You",
    commonWorkplaces: ["Boutique Hotels", "Local Retail Shops", "Restaurants & Cafes", "Event Spaces", "Specialty Food Venues"],
    keySkills: ["Guest Service", "POS Systems", "Problem Resolution", "Event Coordination", "Multitasking"],
    entryRequirements: "100% entry-level friendly. Great communication and welcoming attitude are key.",
    sampleResponsibilities: [
      "Deliver attentive customer service and welcome guests warmly",
      "Process payments, manage guest check-ins, or operate sales registers",
      "Maintain pristine retail floors, dining spaces, or guest areas",
      "Support daily store or hospitality operations during peak hours"
    ],
    interviewTips: [
      "Highlight customer-first enthusiasm, reliability, and flexible availability",
      "Express a passion for creating memorable guest experiences"
    ],
    outreachTip: "Visit general managers during non-peak weekday morning or afternoon hours.",
    relatedCategorySlugs: ["retail-jobs", "restaurant-jobs", "hotel-jobs", "customer-service-jobs"]
  }
];

export function getAllJobCategoryDefinitions(): JobCategoryDefinition[] {
  return JOB_CATEGORIES_REGISTRY;
}

// Map of legacy or display text to canonical category slug
const CATEGORY_ALIAS_MAP: Record<string, string> = {
  "restaurants": "restaurant-jobs",
  "restaurant": "restaurant-jobs",
  "dining": "restaurant-jobs",
  "catering": "restaurant-jobs",
  "food-service": "restaurant-jobs",
  "technology": "tech-agencies",
  "tech": "tech-agencies",
  "it": "tech-agencies",
  "software": "tech-agencies",
  "retail": "retail-jobs",
  "store": "retail-jobs",
  "boutique": "retail-jobs",
  "medical-and-healthcare": "medical-healthcare",
  "medical-clinics": "medical-healthcare",
  "healthcare": "medical-healthcare",
  "health-and-medical": "medical-healthcare",
  "dental-practice": "dental-clinics",
  "dental-office": "dental-clinics",
  "dental": "dental-clinics",
  "plumbing-and-trades": "plumbing-trades",
  "trade-contractors": "plumbing-trades",
  "trades": "plumbing-trades",
  "legal-and-accounting": "legal-accounting",
  "law-and-accounting": "legal-accounting",
  "legal-services": "legal-accounting",
  "tech-and-agencies": "tech-agencies",
  "technology-agencies": "tech-agencies",
  "software-agencies": "tech-agencies",
  "retail-and-hospitality": "retail-hospitality",
  "hospitality-and-retail": "retail-hospitality",
  "hospitality": "retail-hospitality",
  "part-time": "part-time-jobs",
  "entry-level": "entry-level-jobs",
  "warehouse": "warehouse-jobs",
  "receptionist": "receptionist-jobs",
  "driver": "driver-jobs",
  "night-shift": "night-shift-jobs",
  "remote": "remote-jobs",
  "trade-services": "plumbing-trades",
  "construction": "construction-jobs",
  "manufacturing": "manufacturing-jobs",
  "automotive": "auto-repair"
};

export function getJobCategoryBySlug(slug: string): JobCategoryDefinition | null {
  if (!slug) return null;
  const clean = slug.toLowerCase().trim().replace(/--+/g, "-");
  
  // Direct match
  const direct = JOB_CATEGORIES_REGISTRY.find(j => j.slug === clean);
  if (direct) return direct;

  // Alias match
  const aliasSlug = CATEGORY_ALIAS_MAP[clean];
  if (aliasSlug) {
    const aliasMatch = JOB_CATEGORIES_REGISTRY.find(j => j.slug === aliasSlug);
    if (aliasMatch) return aliasMatch;
  }

  // Partial or normalized fallback match
  return JOB_CATEGORIES_REGISTRY.find(j => 
    j.name.toLowerCase() === clean.replace(/-/g, " ") ||
    clean.includes(j.slug) ||
    j.slug.includes(clean)
  ) || null;
}

export function getCategorySlugFromText(text: string): string {
  if (!text) return "medical-healthcare";
  const clean = text.toLowerCase().trim();

  // Check exact category definition match first
  const matchedDef = getJobCategoryBySlug(clean);
  if (matchedDef) return matchedDef.slug;

  if (clean.includes("dental") || clean.includes("dentist") || clean.includes("teeth")) return "dental-clinics";
  if (clean.includes("medical") || clean.includes("healthcare") || clean.includes("hospital")) return "medical-healthcare";
  if (clean.includes("plumb") || clean.includes("trade") || clean.includes("electric") || clean.includes("hvac")) return "plumbing-trades";
  if (clean.includes("legal") || clean.includes("law") || clean.includes("account") || clean.includes("tax")) return "legal-accounting";
  if (clean.includes("tech") || clean.includes("software") || clean.includes("it agency") || clean.includes("web")) return "tech-agencies";
  if (clean.includes("retail") || clean.includes("hospitality") || clean.includes("hotel") || clean.includes("store")) return "retail-hospitality";
  if (clean.includes("restaurant") || clean.includes("diner") || clean.includes("food")) return "restaurant-jobs";
  if (clean.includes("warehouse")) return "warehouse-jobs";
  if (clean.includes("office") || clean.includes("admin")) return "office-jobs";

  return clean.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "medical-healthcare";
}

export function getCategoryBaseName(cat: string | JobCategoryDefinition): string {
  const name = typeof cat === "string" ? cat : cat.name;
  return name.trim().replace(/\s+Jobs?$/i, "");
}

export function sanitizeDuplicateWords(text: string): string {
  if (!text) return text;
  let s = text;
  s = s.replace(/\bnear\s+me\s+near\s+me\b/gi, "Near Me");
  s = s.replace(/\b(Jobs|Employers|Hiring|Internships|Businesses)\s+(?:(?:&|and)\s+)?\1\b/gi, "$1");
  s = s.replace(/\b([a-zA-Z]{2,})\s+\1\b/gi, "$1");
  s = s.replace(/\|\s*biznearyou\b/gi, "| BizNearYou");
  return s.replace(/\s+/g, " ").replace(/\s+\|\s+/g, " | ").trim();
}


